import React, { useEffect, useMemo, useState } from "react"
import { useLocation } from "react-router-dom"
import { apiFetch } from "../utils/apiFetch"
import { useActiveClinic } from "../context/ClinicContext"


const API_BASE = (() => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname
    const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1"

    return isLocalHost ? (["http://localhost", "3000/api"].join(":")) : "/api"
  }

  return "/api"
})()

function getTodayLocal() {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, "0")
  const d = String(now.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

function getDateFromQuery(queryParams) {
  return queryParams.get("date") || queryParams.get("day") || getTodayLocal()
}

function getUserIdFallback() {
  try {
    const rawAuth =
      localStorage.getItem("odontoflow_user") ||
      localStorage.getItem("auth_user") ||
      localStorage.getItem("user")

    if (rawAuth) {
      const parsed = JSON.parse(rawAuth)
      if (parsed?.id) return parsed.id
      if (parsed?.user?.id) return parsed.user.id
    }
  } catch {
    // silencio intencional
  }

  return localStorage.getItem("odontoflow_user_id") || "e922ef71-7119-4f8c-8b73-3c8c42629ebd"
}

function formatMoney(value) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(value || 0))
}

function formatDateTime(value) {
  if (!value) return "-"
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return "-"
  return d.toLocaleString("es-MX", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  })
}

function formatBusinessDate(value) {
  if (!value) return "-"
  const raw = String(value)

  if (raw.includes("T")) {
    const [datePart] = raw.split("T")
    const [year, month, day] = datePart.split("-")
    return `${day}/${month}/${year}`
  }

  if (raw.includes("-")) {
    const [year, month, day] = raw.split("-")
    return `${day}/${month}/${year}`
  }

  return raw
}

function methodLabel(method) {
  const upper = String(method || "").toUpperCase()
  if (upper === "CASH") return "Efectivo"
  if (upper === "CARD") return "Tarjeta"
  if (upper === "TRANSFER") return "Transferencia"
  if (upper === "MIXED") return "Mixto"
  if (upper === "OTHER") return "Otro"
  if (upper === "UNKNOWN") return "Otro"
  return upper || "-"
}

function cashMovementTypeLabel(type) {
  const upper = String(type || "").toUpperCase()

  if (upper === "EXPENSE") return "Gasto"
  if (upper === "OTHER_INCOME") return "Otro ingreso"
  if (upper === "INCOME") return "Otro ingreso"

  return upper || "-"
}

function cashMovementTypeColor(type) {
  const upper = String(type || "").toUpperCase()

  if (upper === "EXPENSE") return "#b91c1c"
  if (upper === "OTHER_INCOME" || upper === "INCOME") return "#166534"

  return "#334155"
}

function defaultCashMovementCategory(type) {
  const upper = String(type || "").toUpperCase()

  if (upper === "OTHER_INCOME" || upper === "INCOME") return "Venta mostrador"
  return "Material dental"
}

function signedCashMovementAmount(item) {
  const amount = Math.abs(Number(item?.amount || 0))

  if (isVoidedCashMovement(item)) return 0

  const rawSigned = Number(item?.signedAmount)

  if (Number.isFinite(rawSigned) && rawSigned !== 0) return rawSigned

  return String(item?.type || "").toUpperCase() === "EXPENSE" ? amount * -1 : amount
}

function isVoidedCashMovement(item) {
  return String(item?.status || "").toUpperCase() === "VOIDED"
}

function originalSignedCashMovementAmount(item) {
  const amount = Math.abs(Number(item?.amount || 0))
  return String(item?.type || "").toUpperCase() === "EXPENSE" ? amount * -1 : amount
}

function cashMovementVoidNote(item) {
  const notes = String(item?.notes || "").trim()

  if (!notes) return ""

  const index = notes.lastIndexOf("[ANULADO")

  if (index >= 0) return notes.slice(index).trim()

  return notes
}

function parseCashMovementVoidInfo(item) {
  const raw = cashMovementVoidNote(item)

  if (!raw) {
    return {
      raw: "",
      reason: "",
      voidedAt: "",
      voidedBy: ""
    }
  }

  const match = raw.match(/^\[ANULADO\s+([^\]]+?)(?:\s+por\s+([^\]]+))?\]\s*(.*)$/)

  if (!match) {
    return {
      raw,
      reason: raw,
      voidedAt: "",
      voidedBy: ""
    }
  }

  return {
    raw,
    voidedAt: String(match[1] || "").trim(),
    voidedBy: String(match[2] || "").trim(),
    reason: String(match[3] || "").trim()
  }
}

function cashMovementCapturedByLabel(item) {
  return item?.createdByName || "Usuario del sistema"
}

function shouldShowCashMovementByStatusFilter(item, filter) {
  if (filter === "VOIDED") return isVoidedCashMovement(item)
  if (filter === "POSTED") return !isVoidedCashMovement(item)
  return true
}

function sessionStatusLabel(status) {
  const upper = String(status || "").toUpperCase()
  if (upper === "OPEN") return "Abierta"
  if (upper === "CLOSED") return "Cerrada"
  if (upper === "PENDING") return "Pendiente"
  return upper || "-"
}

function severityLabel(severity) {
  const upper = String(severity || "").toUpperCase()
  if (upper === "CRITICAL") return "Crítica"
  if (upper === "WARNING") return "Observación"
  return "Sin observaciones"
}

function severityColor(severity) {
  if (severity === "CRITICAL") return "#b91c1c"
  if (severity === "WARNING") return "#92400e"
  return "#166534"
}

function diffColor(value) {
  const number = Number(value || 0)
  if (number === 0) return "#334155"
  if (number > 0) return "#166534"
  return "#b91c1c"
}

export default function CashClosingPage() {
  const location = useLocation()
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const { activeClinicId } = useActiveClinic()

  const storedClinicId =
    typeof window !== "undefined"
      ? localStorage.getItem("odontoflow_clinic_id") || ""
      : ""

  const clinicId =
    activeClinicId && activeClinicId !== "ALL"
      ? activeClinicId
      : !activeClinicId && storedClinicId && storedClinicId !== "ALL"
        ? storedClinicId
        : ""

  const isAllClinicsContext = activeClinicId === "ALL"

  const [userId] = useState(getUserIdFallback())
  const initialDate = useMemo(
    () => getDateFromQuery(queryParams),
    // Solo inicializa estado; abajo sincronizamos cambios de URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const [date, setDate] = useState(initialDate)

  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440
  )

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  const isMobile = viewportWidth < 760

  useEffect(() => {
    const routeDate = queryParams.get("date") || queryParams.get("day") || ""
    if (!routeDate) return
    setDate(routeDate)
    setDateFrom(routeDate)
    setDateTo(routeDate)
  }, [queryParams])

  useEffect(() => {
    setCashMovementForm((prev) => ({
      ...prev,
      movementDate: date || getTodayLocal()
    }))
  }, [date])

  const [summary, setSummary] = useState(null)
  const [loadingSummary, setLoadingSummary] = useState(false)

  const [reconciliation, setReconciliation] = useState(null)
  const [loadingReconciliation, setLoadingReconciliation] = useState(false)
  const [countedCash, setCountedCash] = useState("")

  const [openingAmount, setOpeningAmount] = useState("")
  const [openingNotes, setOpeningNotes] = useState("")
  const [openingCash, setOpeningCash] = useState(false)

  const [closingAmount, setClosingAmount] = useState("")
  const [closingNotes, setClosingNotes] = useState("")
  const [closingCash, setClosingCash] = useState(false)

  const [history, setHistory] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [dateFrom, setDateFrom] = useState(initialDate)
  const [dateTo, setDateTo] = useState(initialDate)

  const [differencesByUser, setDifferencesByUser] = useState([])
  const [loadingDifferencesByUser, setLoadingDifferencesByUser] = useState(false)

  const [alerts, setAlerts] = useState(null)
  const [loadingAlerts, setLoadingAlerts] = useState(false)

  const [cashMovements, setCashMovements] = useState([])
  const [cashMovementSummary, setCashMovementSummary] = useState(null)
  const [loadingCashMovements, setLoadingCashMovements] = useState(false)
  const [savingCashMovement, setSavingCashMovement] = useState(false)
  const [voidingCashMovementId, setVoidingCashMovementId] = useState(null)
  const [cashMovementStatusFilter, setCashMovementStatusFilter] = useState("ALL")
  const [cashMovementNotice, setCashMovementNotice] = useState(null)
  const [cashMovementForm, setCashMovementForm] = useState({
    type: "EXPENSE",
    category: "Material dental",
    concept: "",
    amount: "",
    method: "CASH",
    movementDate: initialDate,
    notes: ""
  })

  const loadSummary = async () => {
    try {
      if (!clinicId) {
        setSummary(null)
        return
      }

      setLoadingSummary(true)

      const params = new URLSearchParams({
        clinicId,
        date,
        _ts: String(Date.now())
      })

      const res = await apiFetch(`${API_BASE}/finance/cash/daily-summary?${params.toString()}`)
      const data = await res.json().catch(() => null)

      if (!res.ok) {
        setSummary(null)
        return
      }

      setSummary(data || null)
    } catch {
      setSummary(null)
    } finally {
      setLoadingSummary(false)
    }
  }

  const loadReconciliation = async () => {
    try {
      if (!clinicId) {
        setReconciliation(null)
        return
      }

      setLoadingReconciliation(true)

      const params = new URLSearchParams({
        clinicId,
        date,
        _ts: String(Date.now())
      })

      const res = await apiFetch(`${API_BASE}/finance/cash/reconciliation?${params.toString()}`)
      const data = await res.json().catch(() => null)

      if (!res.ok) {
        setReconciliation(null)
        return
      }

      setReconciliation(data || null)
    } catch {
      setReconciliation(null)
    } finally {
      setLoadingReconciliation(false)
    }
  }

  const loadCashMovements = async () => {
    try {
      if (!clinicId) {
        setCashMovements([])
        setCashMovementSummary(null)
        return
      }

      setLoadingCashMovements(true)

      const params = new URLSearchParams({
        clinicId,
        date,
        _ts: String(Date.now())
      })

      const res = await apiFetch(`${API_BASE}/finance/cash/movements?${params.toString()}`)
      const data = await res.json().catch(() => null)

      if (!res.ok) {
        setCashMovements([])
        setCashMovementSummary(null)
        return
      }

      setCashMovements(Array.isArray(data?.rows) ? data.rows : [])
      setCashMovementSummary(data?.summary || null)
    } catch {
      setCashMovements([])
      setCashMovementSummary(null)
    } finally {
      setLoadingCashMovements(false)
    }
  }

  const loadHistory = async () => {
    try {
      if (!clinicId) {
        setHistory([])
        return
      }

      setLoadingHistory(true)

      const params = new URLSearchParams({
        clinicId,
        dateFrom,
        dateTo,
        _ts: String(Date.now())
      })

      const res = await apiFetch(`${API_BASE}/finance/cash/session-history?${params.toString()}`)
      const data = await res.json().catch(() => [])

      if (!res.ok) {
        setHistory([])
        return
      }

      setHistory(Array.isArray(data) ? data : [])
    } catch {
      setHistory([])
    } finally {
      setLoadingHistory(false)
    }
  }

  const loadDifferencesByUser = async () => {
    try {
      if (!clinicId) {
        setDifferencesByUser([])
        return
      }

      setLoadingDifferencesByUser(true)

      const params = new URLSearchParams({
        clinicId,
        dateFrom,
        dateTo,
        _ts: String(Date.now())
      })

      const res = await apiFetch(`${API_BASE}/finance/cash/differences-by-user?${params.toString()}`)
      const data = await res.json().catch(() => [])

      if (!res.ok) {
        setDifferencesByUser([])
        return
      }

      setDifferencesByUser(Array.isArray(data) ? data : [])
    } catch {
      setDifferencesByUser([])
    } finally {
      setLoadingDifferencesByUser(false)
    }
  }

  const loadAlerts = async () => {
    try {
      if (!clinicId) {
        setAlerts(null)
        return
      }

      setLoadingAlerts(true)

      const params = new URLSearchParams({
        clinicId,
        dateFrom,
        dateTo,
        _ts: String(Date.now())
      })

      const res = await apiFetch(`${API_BASE}/finance/cash/alerts?${params.toString()}`)
      const data = await res.json().catch(() => null)

      if (!res.ok) {
        setAlerts(null)
        return
      }

      setAlerts(data || null)
    } catch {
      setAlerts(null)
    } finally {
      setLoadingAlerts(false)
    }
  }

  useEffect(() => {
    loadSummary()
    loadReconciliation()
    loadCashMovements()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, date])

  useEffect(() => {
    if (!countedCash) return
    setClosingAmount(countedCash)
  }, [countedCash])

  useEffect(() => {
    loadHistory()
    loadDifferencesByUser()
    loadAlerts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, dateFrom, dateTo])

  const latestSession = summary?.openSession || null
  const sessionStatus = String(latestSession?.status || "").toUpperCase()
  const isOpen = sessionStatus === "OPEN"
  const isClosed = sessionStatus === "CLOSED"

  const payments = summary?.payments || []
  const byMethod = summary?.byMethod || {}

  const openingBase = Number(latestSession?.openingAmount || 0)
  const cashCollected = Number(summary?.byMethod?.CASH || 0)

  const cashMovementRows =
    Array.isArray(cashMovements) && cashMovements.length
      ? cashMovements
      : Array.isArray(summary?.cashMovements)
        ? summary.cashMovements
        : Array.isArray(reconciliation?.cashMovements)
          ? reconciliation.cashMovements
          : []

  const cashMovementSummarySource =
    cashMovementSummary || summary?.cashMovementsSummary || reconciliation?.cashMovementsSummary || {}

  const otherIncomeTotal = Number(
    summary?.otherIncomeTotal ??
    reconciliation?.otherIncomeTotal ??
    cashMovementSummarySource?.otherIncomeTotal ??
    0
  )

  const expenseTotal = Number(
    summary?.expenseTotal ??
    reconciliation?.expenseTotal ??
    cashMovementSummarySource?.expenseTotal ??
    0
  )

  const cashOtherIncomeTotal = Number(
    summary?.cashOtherIncomeTotal ??
    reconciliation?.cashOtherIncomeTotal ??
    cashMovementSummarySource?.cashOtherIncomeTotal ??
    0
  )

  const cashExpenseTotal = Number(
    summary?.cashExpenseTotal ??
    reconciliation?.cashExpenseTotal ??
    cashMovementSummarySource?.cashExpenseTotal ??
    0
  )

  const cashMovementNet = Number(
    summary?.cashMovementNet ??
    reconciliation?.cashMovementNet ??
    otherIncomeTotal - expenseTotal
  )

  const cashMovementCashNet = Number(
    summary?.cashMovementCashNet ??
    reconciliation?.cashMovementCashNet ??
    cashOtherIncomeTotal - cashExpenseTotal
  )

  const expectedCash = Number(
    summary?.expectedPhysicalCash ??
    reconciliation?.expectedPhysicalCash ??
    openingBase + cashCollected + cashMovementCashNet
  )

  const expectedPhysicalCash = Number(reconciliation?.expectedPhysicalCash ?? summary?.expectedPhysicalCash ?? expectedCash ?? 0)

  const hasClosingPreviewValues =
    isOpen && (String(closingAmount).trim() !== "" || String(countedCash).trim() !== "")

  const differencePreview = hasClosingPreviewValues
    ? Number(closingAmount || countedCash || 0) - expectedCash
    : 0

  const cashDifferencePreview = hasClosingPreviewValues
    ? Number(countedCash || 0) - expectedPhysicalCash
    : 0

  const nextActionTitle = !clinicId
    ? isAllClinicsContext
      ? "Selecciona una clínica específica"
      : "Selecciona una clínica"
    : isOpen
      ? "Cerrar caja del día"
      : isClosed
        ? "Caja cerrada"
        : "Abrir caja del día"

  const nextActionHelper = !clinicId
    ? isAllClinicsContext
      ? "Caja diaria se trabaja por clínica, no en la vista de todas las clínicas."
      : "No hay clínica válida seleccionada."
    : isOpen
      ? "La caja está abierta. Concilia efectivo y cierra correctamente."
      : isClosed
        ? "La caja de este día ya fue cerrada. Puedes revisar historial, alertas o imprimir el corte."
        : "No hay apertura registrada para este día. Empieza por aquí."

  const methodCards = [
    { label: "Efectivo", value: byMethod.CASH || 0 },
    { label: "Tarjeta", value: byMethod.CARD || 0 },
    { label: "Transferencia", value: byMethod.TRANSFER || 0 },
    { label: "Otro", value: byMethod.UNKNOWN || 0 }
  ]

  const handleCreateCashMovement = async () => {
    try {
      setCashMovementNotice(null)

      if (!clinicId) {
        setCashMovementNotice({
          tone: "warning",
          title: "Selecciona una clínica",
          text: "Caja diaria registra gastos e ingresos por clínica."
        })
        return
      }

      if (isClosed) {
        setCashMovementNotice({
          tone: "warning",
          title: "Caja cerrada",
          text: "Este día ya fue cerrado. No conviene agregar movimientos después del corte."
        })
        return
      }

      const amount = Number(cashMovementForm.amount || 0)
      const concept = String(cashMovementForm.concept || "").trim()
      const movementDate = cashMovementForm.movementDate || date

      if (!concept) {
        setCashMovementNotice({
          tone: "warning",
          title: "Falta concepto",
          text: "Describe el gasto o ingreso antes de guardarlo."
        })
        return
      }

      if (!Number.isFinite(amount) || amount <= 0) {
        setCashMovementNotice({
          tone: "warning",
          title: "Monto inválido",
          text: "Captura un monto mayor a cero."
        })
        return
      }

      setSavingCashMovement(true)

      const payload = {
        clinicId,
        type: cashMovementForm.type || "EXPENSE",
        category: cashMovementForm.category?.trim() || defaultCashMovementCategory(cashMovementForm.type),
        concept,
        amount,
        method: cashMovementForm.method || "CASH",
        movementDate,
        notes: cashMovementForm.notes?.trim() || ""
      }

      const res = await apiFetch(`${API_BASE}/finance/cash/movement`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        setCashMovementNotice({
          tone: "danger",
          title: "No se pudo guardar",
          text: data?.message || "El movimiento no pudo registrarse."
        })
        return
      }

      setCashMovementForm((prev) => ({
        ...prev,
        concept: "",
        amount: "",
        notes: "",
        category: defaultCashMovementCategory(prev.type),
        movementDate: date || getTodayLocal()
      }))

      setCashMovementNotice({
        tone: "success",
        title: "Movimiento registrado",
        text: "Caja diaria se actualizó con el nuevo gasto o ingreso."
      })

      await loadCashMovements()
      await loadSummary()
      await loadReconciliation()
      await loadCashMovements()
      await loadHistory()
      await loadAlerts()
    } catch {
      setCashMovementNotice({
        tone: "danger",
        title: "Error",
        text: "No se pudo registrar el movimiento."
      })
    } finally {
      setSavingCashMovement(false)
    }
  }

  const handleVoidCashMovement = async (item) => {
    try {
      setCashMovementNotice(null)

      if (!clinicId) {
        setCashMovementNotice({
          tone: "warning",
          title: "Selecciona una clínica",
          text: "No se puede anular un movimiento sin clínica seleccionada."
        })
        return
      }

      if (isClosed) {
        setCashMovementNotice({
          tone: "warning",
          title: "Caja cerrada",
          text: "Este día ya fue cerrado. Para mantener el corte íntegro, no se anulan movimientos después del cierre."
        })
        return
      }

      const movementId = item?.id

      if (!movementId) {
        setCashMovementNotice({
          tone: "danger",
          title: "Movimiento inválido",
          text: "No se encontró el identificador del movimiento."
        })
        return
      }

      const movementLabel = item?.concept || "movimiento manual"
      const amountLabel = formatMoney(Math.abs(Number(item?.amount || 0)))
      const defaultReason = "Anulación de " + cashMovementTypeLabel(item?.type).toLowerCase() + ": " + movementLabel

      const reason = window.prompt("Motivo de anulación", defaultReason)

      if (reason === null) {
        return
      }

      const cleanReason = String(reason || "").trim()

      if (!cleanReason) {
        setCashMovementNotice({
          tone: "warning",
          title: "Falta motivo",
          text: "Captura un motivo para dejar rastro de auditoría."
        })
        return
      }

      const confirmed = window.confirm(
        "Se anulará " + movementLabel + " por " + amountLabel + ". Ya no impactará la caja. ¿Continuar?"
      )

      if (!confirmed) {
        return
      }

      setVoidingCashMovementId(movementId)

      const res = await apiFetch(`${API_BASE}/finance/cash/movement/${movementId}/void`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          clinicId,
          reason: cleanReason
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        setCashMovementNotice({
          tone: "danger",
          title: "No se pudo anular",
          text: data?.message || "El movimiento no pudo anularse."
        })
        return
      }

      setCashMovementNotice({
        tone: "success",
        title: "Movimiento anulado",
        text: data?.message || "El movimiento dejó de impactar la caja."
      })

      await loadCashMovements()
      await loadSummary()
      await loadReconciliation()
      await loadHistory()
      await loadAlerts()
    } catch {
      setCashMovementNotice({
        tone: "danger",
        title: "Error",
        text: "No se pudo anular el movimiento."
      })
    } finally {
      setVoidingCashMovementId(null)
    }
  }

  const handleOpenCash = async () => {
    try {
      if (!clinicId) {
        alert("No hay una clínica válida seleccionada.")
        return
      }

      setOpeningCash(true)

      const res = await apiFetch(`${API_BASE}/finance/cash/open-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          clinicId,
          userId,
          businessDate: date,
          openingAmount: Number(openingAmount || 0),
          notes: openingNotes || ""
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo abrir la caja")
        return
      }

      alert("Caja abierta correctamente")
      setOpeningAmount("")
      setOpeningNotes("")
      await loadSummary()
      await loadReconciliation()
      await loadCashMovements()
      await loadHistory()
      await loadDifferencesByUser()
      await loadAlerts()
    } catch {
      alert("Error al abrir la caja")
    } finally {
      setOpeningCash(false)
    }
  }

  const handleCloseCash = async () => {
    try {
      if (!clinicId) {
        alert("No hay una clínica válida seleccionada.")
        return
      }

      if (!summary?.openSession?.id) {
        alert("No hay una caja abierta para cerrar.")
        return
      }

      if (!closingAmount || Number(closingAmount) < 0) {
        alert("Captura un monto de cierre válido.")
        return
      }

      setClosingCash(true)

      const res = await apiFetch(`${API_BASE}/finance/cash/close-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          clinicId,
          userId,
          businessDate: date,
          countedCash: Number(countedCash || 0),
          closingAmount: Number(closingAmount || countedCash || 0),
          notes: closingNotes || ""
        })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo cerrar la caja")
        return
      }

      alert("Caja cerrada correctamente")
      setClosingAmount("")
      setClosingNotes("")
      setCountedCash("")
      await loadSummary()
      await loadReconciliation()
      await loadCashMovements()
      await loadHistory()
      await loadDifferencesByUser()
      await loadAlerts()
    } catch {
      alert("Error al cerrar la caja")
    } finally {
      setClosingCash(false)
    }
  }

  const handlePrintCash = async () => {
    try {
      const res = await apiFetch(
        `${API_BASE}/finance/cash/printable-summary?clinicId=${clinicId}&date=${date}`
      )

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No hay corte cerrado para ese día")
        return
      }

      const win = window.open("", "_blank")
      if (!win) {
        alert("No se pudo abrir la ventana de impresión")
        return
      }

      const printableManualMovements = Array.isArray(data.manualMovements) ? data.manualMovements : []

      const printableActiveManualMovements = Array.isArray(data.activeManualMovements)
        ? data.activeManualMovements
        : printableManualMovements.filter((item) => !isVoidedCashMovement(item))

      const printableVoidedManualMovements = Array.isArray(data.voidedManualMovements)
        ? data.voidedManualMovements
        : printableManualMovements.filter((item) => isVoidedCashMovement(item))

      const escapeHtml = (value) =>
        String(value ?? "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;")

      const renderPrintableEmpty = (colspan) =>
        "<tr><td colspan=\"" + colspan + "\" class=\"empty\">Sin registros.</td></tr>"

      const renderPrintableActiveManualRows = () => {
        if (!printableActiveManualMovements.length) return renderPrintableEmpty(6)

        return printableActiveManualMovements.map((item) => {
          const signedAmount = Number(item.signedAmount ?? originalSignedCashMovementAmount(item))
          const sign = signedAmount < 0 ? "-" : "+"
          const toneClass = signedAmount < 0 ? "danger" : "success"

          return [
            "<tr>",
            "<td>" + escapeHtml(cashMovementTypeLabel(item.type)) + "</td>",
            "<td><strong>" + escapeHtml(item.concept || "-") + "</strong><div class=\"small\">" + escapeHtml(item.category || "Sin categoría") + "</div></td>",
            "<td>" + escapeHtml(methodLabel(item.method)) + "</td>",
            "<td class=\"right strong " + toneClass + "\">" + sign + escapeHtml(formatMoney(Math.abs(signedAmount))) + "</td>",
            "<td>" + escapeHtml(item.createdByName || "Usuario del sistema") + "<div class=\"small\">" + escapeHtml(formatDateTime(item.createdAt)) + "</div></td>",
            "<td>" + escapeHtml(item.notes || "-") + "</td>",
            "</tr>"
          ].join("")
        }).join("")
      }

      const renderPrintableVoidedManualRows = () => {
        if (!printableVoidedManualMovements.length) return renderPrintableEmpty(6)

        return printableVoidedManualMovements.map((item) => {
          const voidInfo = parseCashMovementVoidInfo(item)
          const originalAmount = Number(item.originalSignedAmount ?? originalSignedCashMovementAmount(item))
          const sign = originalAmount < 0 ? "-" : "+"

          return [
            "<tr class=\"voided\">",
            "<td><span class=\"badge\">ANULADO</span><div class=\"small\">" + escapeHtml(cashMovementTypeLabel(item.type)) + "</div></td>",
            "<td><strong>" + escapeHtml(item.concept || "-") + "</strong><div class=\"small\">" + escapeHtml(item.category || "Sin categoría") + "</div></td>",
            "<td>" + escapeHtml(methodLabel(item.method)) + "</td>",
            "<td class=\"right strong\"><span class=\"strike\">" + sign + escapeHtml(formatMoney(Math.abs(originalAmount))) + "</span><div class=\"small\">Sin impacto</div></td>",
            "<td>" + escapeHtml(voidInfo.reason || "-") + "<div class=\"small\">" + escapeHtml(voidInfo.voidedBy ? "Anulado por " + voidInfo.voidedBy : "Anulado") + (voidInfo.voidedAt ? " · " + escapeHtml(formatDateTime(voidInfo.voidedAt)) : "") + "</div></td>",
            "<td>" + escapeHtml(item.createdByName || "Usuario del sistema") + "<div class=\"small\">" + escapeHtml(formatDateTime(item.createdAt)) + "</div></td>",
            "</tr>"
          ].join("")
        }).join("")
      }

      win.document.write(`
        <html>
          <head>
            <title>Corte de caja</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 28px;
                color: #111827;
              }
              h1 { margin-bottom: 4px; }
              .muted { color: #64748b; margin-bottom: 18px; }
              .box {
                border: 1px solid #e2e8f0;
                border-radius: 12px;
                padding: 14px;
                margin-bottom: 14px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 12px;
              }
              th, td {
                border: 1px solid #e2e8f0;
                padding: 8px;
                text-align: left;
                vertical-align: top;
              }
              th {
                background: #f8fafc;
                font-size: 11px;
                text-transform: uppercase;
              }
              .right { text-align: right; }
              .strong { font-weight: 800; }
              .small {
                color: #64748b;
                font-size: 11px;
                margin-top: 2px;
              }
              .success { color: #166534; }
              .danger { color: #b91c1c; }
              .voided {
                color: #64748b;
                background: #f8fafc;
              }
              .strike {
                color: #94a3b8;
                text-decoration: line-through;
              }
              .badge {
                display: inline-block;
                border: 1px solid #cbd5e1;
                background: #f1f5f9;
                color: #64748b;
                border-radius: 999px;
                padding: 3px 7px;
                font-size: 10px;
                font-weight: 900;
                letter-spacing: 0.4px;
                margin-bottom: 3px;
              }
              .empty {
                color: #64748b;
                text-align: center;
                padding: 14px;
              }
              .footer {
                margin-top: 22px;
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 20px;
              }
              .signature {
                border-top: 1px solid #94a3b8;
                padding-top: 8px;
                color: #475569;
                text-align: center;
                margin-top: 36px;
              }
            </style>
          </head>
          <body>
            <h1>${data.clinic || "Clínica"}</h1>
            <div class="muted">Corte de caja — ${data.date || "-"}</div>

            <div class="box">
              <strong>Resumen del corte</strong>
              <p>
                Apertura: ${Number(data.openingAmount || 0).toFixed(2)} <br/>
                Efectivo cobrado: ${Number(data.cashCollected || data?.totals?.CASH || 0).toFixed(2)} <br/>
                Otros ingresos efectivo: ${Number(data.cashOtherIncomeTotal || 0).toFixed(2)} <br/>
                Gastos efectivo: -${Number(data.cashExpenseTotal || 0).toFixed(2)} <br/>
                Impacto efectivo manual: ${Number(data.cashMovementCashNet || 0).toFixed(2)} <br/>
                Esperado: ${Number(data.expectedCash || 0).toFixed(2)} <br/>
                Conteo físico: $${Number(data.countedCash || 0).toFixed(2)} <br/>
                Cierre: $${Number(data.closingAmount || 0).toFixed(2)} <br/>
                Diferencia: $${Number(data.difference || 0).toFixed(2)}
              </p>
            </div>

            <div class="box">
              <strong>Ingresos por método</strong>
              <table>
                <tr>
                  <th>Método</th>
                  <th class="right">Monto</th>
                </tr>
                ${Object.entries(data.byMethod || {})
                  .map(
                    ([method, amount]) => `
                      <tr>
                        <td>${methodLabel(method)}</td>
                        <td class="right">$${Number(amount || 0).toFixed(2)}</td>
                      </tr>
                    `
                  )
                  .join("")}
              </table>
            </div>

            <div class="box">
              <strong>Movimientos del día</strong>
              <table>
                <tr>
                  <th>Hora</th>
                  <th>Paciente</th>
                  <th>Método</th>
                  <th class="right">Monto</th>
                </tr>
                ${(data.movements || [])
                  .map(
                    (m) => `
                      <tr>
                        <td>${new Date(m.createdAt).toLocaleTimeString("es-MX", {
                          hour: "2-digit",
                          minute: "2-digit"
                        })}</td>
                        <td>${m.patientName || ""}</td>
                        <td>${methodLabel(m.method)}</td>
                        <td class="right">$${Number(m.amount || 0).toFixed(2)}</td>
                      </tr>
                    `
                  )
                  .join("")}
              </table>
            </div>

            <div class="box">
              <strong>Gastos y otros ingresos activos</strong>
              <table>
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Concepto</th>
                    <th>Método</th>
                    <th class="right">Monto</th>
                    <th>Capturado por</th>
                    <th>Notas</th>
                  </tr>
                </thead>
                <tbody>${renderPrintableActiveManualRows()}</tbody>
              </table>
            </div>

            <div class="box">
              <strong>Movimientos anulados informativos</strong>
              <p class="muted">Estos registros no impactan la caja; se muestran solo como rastro de auditoría.</p>
              <table>
                <thead>
                  <tr>
                    <th>Estado</th>
                    <th>Concepto</th>
                    <th>Método</th>
                    <th class="right">Monto original</th>
                    <th>Motivo / anulación</th>
                    <th>Capturado por</th>
                  </tr>
                </thead>
                <tbody>${renderPrintableVoidedManualRows()}</tbody>
              </table>
            </div>

            <div class="footer">
              <div class="signature">Entrega caja</div>
              <div class="signature">Recibe / valida</div>
            </div>

            <script>window.print();</script>
          </body>
        </html>
      `)

      win.document.close()
    } catch {
      alert("Error al generar corte")
    }
  }

  const pageStyle = {
    padding: isMobile ? 10 : 20,
    background: "#f8fafc",
    minHeight: "100vh",
    display: "grid",
    gap: isMobile ? 10 : 16,
    overflowX: "hidden"
  }

  const heroGridStyle = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : "minmax(220px, 260px) repeat(auto-fit, minmax(180px, 1fr))",
    gap: isMobile ? 10 : 12
  }

  const twoColumnStyle = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "minmax(320px, 0.9fr) minmax(0, 1.1fr)",
    gap: isMobile ? 10 : 16,
    minWidth: 0
  }

  const historyGridStyle = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1.15fr) minmax(320px, 0.85fr)",
    gap: isMobile ? 10 : 16,
    minWidth: 0
  }

  return (
    <div style={pageStyle}>
      <HeroCard
        title="Caja diaria"
        subtitle="Apertura, conciliación, cierre e impresión del día con foco operativo."
        actions={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={handlePrintCash}
              disabled={!clinicId}
              style={{
                ...buttonStyle,
                background: "#ffffff"
              }}
            >
              Imprimir corte
            </button>

            {!isOpen && !isClosed ? (
              <button
                onClick={handleOpenCash}
                disabled={openingCash || !clinicId}
                style={{
                  ...buttonStyle,
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  color: "#1d4ed8",
                  cursor: openingCash || !clinicId ? "not-allowed" : "pointer",
                  opacity: openingCash || !clinicId ? 0.7 : 1
                }}
              >
                {openingCash ? "Abriendo..." : "Abrir caja"}
              </button>
            ) : (
              <button
                onClick={handleCloseCash}
                disabled={closingCash || !clinicId}
                style={{
                  ...buttonStyle,
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  color: "#1d4ed8",
                  cursor: closingCash || !clinicId ? "not-allowed" : "pointer",
                  opacity: closingCash || !clinicId ? 0.7 : 1
                }}
              >
                {closingCash ? "Cerrando..." : "Cerrar caja"}
              </button>
            )}
          </div>
        }
      >
        <div style={{ display: "grid", gap: 14 }}>
          <div
            style={{
              ...heroGridStyle
            }}
          >
            <div>
              <FieldLabel>Fecha de trabajo</FieldLabel>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={inputStyle}
              />
            </div>

            <HeroMetric
              label="Estado"
              value={isOpen ? "Caja abierta" : isClosed ? "Caja cerrada" : "Sin apertura"}
              tone={isOpen ? "success" : isClosed ? "neutral" : "warning"}
            />
            <HeroMetric label="Efectivo esperado" value={formatMoney(expectedPhysicalCash)} />
            <HeroMetric label="Cobrado del día" value={formatMoney(summary?.totalCollected || 0)} />
            <HeroMetric label="Pagos registrados" value={String(payments.length || 0)} />
          </div>

          <div
            style={{
              border: "1px solid #e2e8f0",
              background: "#ffffff",
              borderRadius: 16,
              padding: 14
            }}
          >
            <div
              style={{
                fontSize: 11.5,
                color: "#64748b",
                textTransform: "uppercase",
                fontWeight: 900,
                letterSpacing: 0.35
              }}
            >
              Próximo paso
            </div>

            <div
              style={{
                marginTop: 6,
                fontSize: 18,
                fontWeight: 900,
                color: "#0f172a",
                letterSpacing: "-0.02em"
              }}
            >
              {nextActionTitle}
            </div>

            <div
              style={{
                marginTop: 4,
                fontSize: 13,
                color: "#64748b",
                lineHeight: 1.45
              }}
            >
              {nextActionHelper}
            </div>
          </div>
        </div>
      </HeroCard>

      {!clinicId ? (
        <StateCard tone="danger" title={isAllClinicsContext ? "Selecciona una clínica" : "Sin clínica activa"}>
          {isAllClinicsContext
            ? "Caja diaria es operativa por clínica. Selecciona una clínica específica para abrir, conciliar o cerrar caja."
            : "No hay una clínica válida seleccionada para trabajar caja diaria."}
        </StateCard>
      ) : null}

      <div
        style={{
          ...twoColumnStyle
        }}
      >
        <SectionCard
          title={!isOpen && !isClosed ? "Apertura de caja" : isClosed ? "Caja cerrada" : "Cierre de caja"}
          subtitle={
            !isOpen && !isClosed
              ? "Empieza el día registrando el fondo inicial."
              : isClosed
                ? "Este día ya tiene un cierre registrado."
                : "Concilia efectivo y registra el cierre del día."
          }
        >
          {!isOpen && !isClosed ? (
            <div style={{ display: "grid", gap: 12 }}>
              <div>
                <FieldLabel>Monto inicial</FieldLabel>
                <input
                  type="number"
                  placeholder="Monto inicial en caja"
                  value={openingAmount}
                  onChange={(e) => setOpeningAmount(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div>
                <FieldLabel>Notas de apertura</FieldLabel>
                <textarea
                  placeholder="Observaciones de apertura"
                  value={openingNotes}
                  onChange={(e) => setOpeningNotes(e.target.value)}
                  style={textareaStyle}
                />
              </div>

              <button
                onClick={handleOpenCash}
                disabled={openingCash}
                style={{
                  ...buttonStyle,
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  color: "#1d4ed8",
                  cursor: openingCash ? "not-allowed" : "pointer",
                  opacity: openingCash ? 0.7 : 1
                }}
              >
                {openingCash ? "Abriendo..." : "Abrir caja"}
              </button>
            </div>
          ) : isClosed ? (
            <div style={{ display: "grid", gap: 12 }}>
              <InlineInfoGrid
                items={[
                  ["Apertura", formatMoney(latestSession?.openingAmount || 0)],
                  ["Cierre", formatMoney(latestSession?.closingAmount || 0)],
                  ["Esperado", formatMoney(latestSession?.expectedCash || expectedCash)],
                  ["Diferencia", formatMoney(latestSession?.difference || 0)],
                  ["Abierta", formatDateTime(latestSession?.openedAt)],
                  ["Cerrada", formatDateTime(latestSession?.closedAt)]
                ]}
              />

              <StateCard tone="neutral" title="Caja del día cerrada">
                Este día ya tiene un cierre registrado. Puedes imprimir el corte o revisar el historial.
              </StateCard>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              <InlineInfoGrid
                items={[
                  ["Apertura", formatMoney(latestSession?.openingAmount || 0)],
                  ["Abierta desde", formatDateTime(latestSession?.openedAt)],
                  ["Día de caja", formatBusinessDate(latestSession?.businessDate)],
                  ["Esperado", formatMoney(expectedCash)]
                ]}
              />

              <div>
                <FieldLabel>Conteo físico de efectivo</FieldLabel>
                <input
                  type="number"
                  placeholder="Monto contado"
                  value={countedCash}
                  onChange={(e) => setCountedCash(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div>
                <FieldLabel>Monto de cierre</FieldLabel>
                <input
                  type="number"
                  placeholder="Monto final de cierre"
                  value={closingAmount}
                  onChange={(e) => setClosingAmount(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <DifferenceCard
                title="Diferencia estimada"
                value={differencePreview}
                helper="Cierre capturado contra efectivo esperado"
              />

              <div>
                <FieldLabel>Notas de cierre</FieldLabel>
                <textarea
                  placeholder="Observaciones de cierre"
                  value={closingNotes}
                  onChange={(e) => setClosingNotes(e.target.value)}
                  style={textareaStyle}
                />
              </div>

              <button
                onClick={handleCloseCash}
                disabled={closingCash}
                style={{
                  ...buttonStyle,
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  color: "#1d4ed8",
                  cursor: closingCash ? "not-allowed" : "pointer",
                  opacity: closingCash ? 0.7 : 1
                }}
              >
                {closingCash ? "Cerrando..." : "Cerrar caja"}
              </button>
            </div>
          )}
        </SectionCard>

        <SectionCard
          title="Conciliación del día"
          subtitle="Compara sistema contra efectivo físico y detecta diferencias."
        >
          {loadingReconciliation ? (
            <StateLine text="Cargando conciliación..." />
          ) : !reconciliation ? (
            <StateLine text="Sin información de conciliación para este día." />
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 12
                }}
              >
                <CompactMetric label="Fondo inicial" value={formatMoney(reconciliation.openingAmount || reconciliation.openingCash || 0)} />
                <CompactMetric label="Efectivo cobrado" value={formatMoney(reconciliation.cashCollected || reconciliation?.totals?.CASH || 0)} />
                <CompactMetric label="Efectivo esperado" value={formatMoney(expectedPhysicalCash)} />
                <CompactMetric
                  label="Conteo físico"
                  value={formatMoney(isClosed ? latestSession?.countedCash || 0 : countedCash || 0)}
                  tone={isClosed || countedCash ? "neutral" : "warning"}
                />
              </div>

              <DifferenceCard
                title="Diferencia física"
                value={cashDifferencePreview}
                helper="Conteo físico contra efectivo esperado"
              />

              <div style={{ fontSize: 12.5, color: "#64748b", lineHeight: 1.4 }}>
                Tarjeta y transferencia se concilian contra sistema; el conteo físico aplica solo sobre efectivo.
              </div>
            </div>
          )}
        </SectionCard>
      </div>

      <SectionCard
        title="Gastos y otros ingresos"
        subtitle="Registra movimientos de caja no ligados a pacientes o citas."
      >
        <div style={{ display: "grid", gap: 14 }}>
          <InlineInfoGrid
            items={[
              ["Otros ingresos", formatMoney(otherIncomeTotal)],
              ["Gastos", formatMoney(expenseTotal)],
              ["Impacto total", formatMoney(cashMovementNet)],
              ["Impacto en efectivo", formatMoney(cashMovementCashNet)]
            ]}
          />

          {cashMovementNotice ? (
            <StateCard tone={cashMovementNotice.tone || "neutral"} title={cashMovementNotice.title}>
              {cashMovementNotice.text}
            </StateCard>
          ) : null}

          {isClosed ? (
            <StateCard tone="neutral" title="Día cerrado">
              Este corte ya fue cerrado. Los movimientos manuales se consultan aquí, pero no se agregan después del cierre.
            </StateCard>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
                gap: 12,
                alignItems: "end"
              }}
            >
              <div>
                <FieldLabel>Tipo</FieldLabel>
                <select
                  value={cashMovementForm.type}
                  onChange={(e) => {
                    const nextType = e.target.value
                    setCashMovementForm((prev) => ({
                      ...prev,
                      type: nextType,
                      category: defaultCashMovementCategory(nextType)
                    }))
                  }}
                  style={inputStyle}
                  disabled={!clinicId || savingCashMovement}
                >
                  <option value="EXPENSE">Gasto</option>
                  <option value="OTHER_INCOME">Otro ingreso</option>
                </select>
              </div>

              <div>
                <FieldLabel>Categoría</FieldLabel>
                <input
                  value={cashMovementForm.category}
                  onChange={(e) => setCashMovementForm((prev) => ({ ...prev, category: e.target.value }))}
                  placeholder={cashMovementForm.type === "EXPENSE" ? "Material dental" : "Venta mostrador"}
                  style={inputStyle}
                  disabled={!clinicId || savingCashMovement}
                />
              </div>

              <div>
                <FieldLabel>Método</FieldLabel>
                <select
                  value={cashMovementForm.method}
                  onChange={(e) => setCashMovementForm((prev) => ({ ...prev, method: e.target.value }))}
                  style={inputStyle}
                  disabled={!clinicId || savingCashMovement}
                >
                  <option value="CASH">Efectivo</option>
                  <option value="CARD">Tarjeta</option>
                  <option value="TRANSFER">Transferencia</option>
                  <option value="MIXED">Mixto</option>
                  <option value="OTHER">Otro</option>
                </select>
              </div>

              <div>
                <FieldLabel>Concepto</FieldLabel>
                <input
                  value={cashMovementForm.concept}
                  onChange={(e) => setCashMovementForm((prev) => ({ ...prev, concept: e.target.value }))}
                  placeholder={cashMovementForm.type === "EXPENSE" ? "Compra de resina" : "Venta de cepillo"}
                  style={inputStyle}
                  disabled={!clinicId || savingCashMovement}
                />
              </div>

              <div>
                <FieldLabel>Monto</FieldLabel>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={cashMovementForm.amount}
                  onChange={(e) => setCashMovementForm((prev) => ({ ...prev, amount: e.target.value }))}
                  placeholder="0.00"
                  style={inputStyle}
                  disabled={!clinicId || savingCashMovement}
                />
              </div>

              <div>
                <FieldLabel>Fecha</FieldLabel>
                <input
                  type="date"
                  value={cashMovementForm.movementDate}
                  onChange={(e) => setCashMovementForm((prev) => ({ ...prev, movementDate: e.target.value }))}
                  style={inputStyle}
                  disabled={!clinicId || savingCashMovement}
                />
              </div>

              <div style={{ gridColumn: isMobile ? "auto" : "1 / span 2" }}>
                <FieldLabel>Notas</FieldLabel>
                <textarea
                  value={cashMovementForm.notes}
                  onChange={(e) => setCashMovementForm((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Detalle opcional para auditoría de caja"
                  style={{
                    ...inputStyle,
                    height: "auto",
                    minHeight: 74,
                    resize: "vertical",
                    lineHeight: 1.45
                  }}
                  disabled={!clinicId || savingCashMovement}
                />
              </div>

              <button
                type="button"
                onClick={handleCreateCashMovement}
                disabled={!clinicId || savingCashMovement || isClosed}
                style={{
                  border: "none",
                  borderRadius: 14,
                  padding: "12px 16px",
                  fontWeight: 900,
                  color: "#ffffff",
                  background: cashMovementForm.type === "EXPENSE" ? "#b91c1c" : "#166534",
                  cursor: !clinicId || savingCashMovement || isClosed ? "not-allowed" : "pointer",
                  opacity: !clinicId || savingCashMovement || isClosed ? 0.7 : 1,
                  minHeight: 46
                }}
              >
                {savingCashMovement
                  ? "Guardando..."
                  : cashMovementForm.type === "EXPENSE"
                    ? "Guardar gasto"
                    : "Guardar ingreso"}
              </button>
            </div>
          )}

          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ fontWeight: 900, color: "#0f172a" }}>
              Movimientos manuales del día
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
                marginBottom: 12
              }}
            >
              {[
                { value: "ALL", label: "Todos los movimientos" },
                { value: "POSTED", label: "Activos" },
                { value: "VOIDED", label: "Anulados" }
              ].map((option) => {
                const active = cashMovementStatusFilter === option.value

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setCashMovementStatusFilter(option.value)}
                    style={{
                      border: active ? "1px solid #1d4ed8" : "1px solid #cbd5e1",
                      background: active ? "#eff6ff" : "#ffffff",
                      color: active ? "#1d4ed8" : "#475569",
                      borderRadius: 999,
                      padding: "7px 11px",
                      fontSize: 12,
                      fontWeight: 900,
                      cursor: "pointer"
                    }}
                  >
                    {option.label}
                  </button>
                )
              })}
            </div>

            {loadingCashMovements ? (
              <StateLine text="Cargando gastos e ingresos..." />
            ) : (
              <SimpleTable
                mobile={isMobile}
                headers={["Tipo", "Concepto", "Método", "Monto", "Fecha", "Acciones"]}
                rows={cashMovementRows
                  .filter((item) => shouldShowCashMovementByStatusFilter(item, cashMovementStatusFilter))
                  .map((item) => {
                  const signedAmount = signedCashMovementAmount(item)
                  const originalSignedAmount = originalSignedCashMovementAmount(item)
                  const isVoidedMovement = isVoidedCashMovement(item)
                  const voidNote = cashMovementVoidNote(item)
                  const voidInfo = parseCashMovementVoidInfo(item)
                  const isVoidingThisMovement = voidingCashMovementId === item.id
                  const cannotVoidMovement = isClosed || isVoidingThisMovement || isVoidedMovement || !item?.id
                  const voidedTextStyle = isVoidedMovement
                    ? { color: "#94a3b8", textDecoration: "line-through" }
                    : {}

                  return [
                    <div style={{ display: "grid", gap: 4 }}>
                      <span
                        style={{
                          fontWeight: 900,
                          color: isVoidedMovement ? "#94a3b8" : cashMovementTypeColor(item.type),
                          textDecoration: isVoidedMovement ? "line-through" : "none"
                        }}
                      >
                        {cashMovementTypeLabel(item.type)}
                      </span>

                      {isVoidedMovement ? (
                        <span
                          style={{
                            width: "fit-content",
                            border: "1px solid #cbd5e1",
                            background: "#f1f5f9",
                            color: "#64748b",
                            borderRadius: 999,
                            padding: "3px 7px",
                            fontSize: 11,
                            fontWeight: 950,
                            letterSpacing: 0.3
                          }}
                        >
                          ANULADO
                        </span>
                      ) : null}
                    </div>,
                    <div>
                      <div style={{ fontWeight: 800, color: isVoidedMovement ? "#94a3b8" : "#0f172a", ...voidedTextStyle }}>
                        {item.concept || "-"}
                      </div>
                      <div style={{ color: isVoidedMovement ? "#94a3b8" : "#64748b", fontSize: 12, textDecoration: isVoidedMovement ? "line-through" : "none" }}>
                        {item.category || "Sin categoría"}
                      </div>

                      {isVoidedMovement && voidInfo.reason ? (
                        <div style={{ color: "#64748b", fontSize: 12, marginTop: 4, lineHeight: 1.35 }}>
                          Motivo: {voidInfo.reason}
                        </div>
                      ) : null}

                      {isVoidedMovement && (voidInfo.voidedBy || voidInfo.voidedAt) ? (
                        <div style={{ color: "#64748b", fontSize: 12, marginTop: 2, lineHeight: 1.35 }}>
                          Anulado{voidInfo.voidedBy ? " por " + voidInfo.voidedBy : ""}{voidInfo.voidedAt ? " · " + formatDateTime(voidInfo.voidedAt) : ""}
                        </div>
                      ) : null}

                      {isVoidedMovement && !voidInfo.reason && voidNote ? (
                        <div style={{ color: "#64748b", fontSize: 12, marginTop: 4, lineHeight: 1.35 }}>
                          {voidNote}
                        </div>
                      ) : null}
                    </div>,
                    <span style={{ fontWeight: 800, color: isVoidedMovement ? "#94a3b8" : "#0f172a", textDecoration: isVoidedMovement ? "line-through" : "none" }}>
                      {methodLabel(item.method)}
                    </span>,
                    <div style={{ display: "grid", gap: 2 }}>
                      <span
                        style={{
                          fontWeight: 900,
                          color: isVoidedMovement ? "#94a3b8" : signedAmount < 0 ? "#b91c1c" : "#166534",
                          textDecoration: isVoidedMovement ? "line-through" : "none"
                        }}
                      >
                        {isVoidedMovement
                          ? originalSignedAmount < 0
                            ? "-"
                            : "+"
                          : signedAmount < 0
                            ? "-"
                            : "+"}
                        {formatMoney(Math.abs(isVoidedMovement ? originalSignedAmount : signedAmount))}
                      </span>

                      {isVoidedMovement ? (
                        <span style={{ color: "#64748b", fontSize: 12, fontWeight: 850 }}>
                          Sin impacto
                        </span>
                      ) : null}
                    </div>,
                    <div>
                      <div style={{ fontWeight: 800, color: isVoidedMovement ? "#94a3b8" : "#0f172a", textDecoration: isVoidedMovement ? "line-through" : "none" }}>
                        {formatBusinessDate(item.movementDate)}
                      </div>
                      <div style={{ color: isVoidedMovement ? "#94a3b8" : "#64748b", fontSize: 12 }}>
                        Capturado: {formatDateTime(item.createdAt)}
                      </div>
                      <div style={{ color: isVoidedMovement ? "#94a3b8" : "#64748b", fontSize: 12 }}>
                        Por: {cashMovementCapturedByLabel(item)}
                      </div>
                    </div>,
                    <button
                      type="button"
                      onClick={() => handleVoidCashMovement(item)}
                      disabled={cannotVoidMovement}
                      title={isVoidedMovement ? "Movimiento anulado" : isClosed ? "No se puede anular después del cierre" : "Anular movimiento"}
                      style={{
                        border: isVoidedMovement ? "1px solid #cbd5e1" : "1px solid #fecaca",
                        background: isVoidedMovement ? "#f8fafc" : "#fff1f2",
                        color: isVoidedMovement ? "#64748b" : "#b91c1c",
                        borderRadius: 999,
                        padding: "6px 10px",
                        fontSize: 12,
                        fontWeight: 900,
                        cursor: cannotVoidMovement ? "not-allowed" : "pointer",
                        opacity: cannotVoidMovement ? 0.55 : 1,
                        whiteSpace: "nowrap"
                      }}
                    >
                      {isVoidedMovement ? "Anulado" : isVoidingThisMovement ? "Anulando..." : "Anular"}
                    </button>
                  ]
                })}
                emptyLabel={
                  cashMovementStatusFilter === "VOIDED"
                    ? "No hay movimientos anulados para este día."
                    : cashMovementStatusFilter === "POSTED"
                      ? "No hay movimientos activos para este día."
                      : "No hay gastos u otros ingresos registrados para este día."
                }
              />
            )}
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Ingresos por método"
        subtitle="Lectura rápida del comportamiento del dinero registrado hoy."
      >
        {loadingSummary ? (
          <StateLine text="Cargando resumen..." />
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12
            }}
          >
            {methodCards.map((item) => (
              <CompactMetric
                key={item.label}
                label={item.label}
                value={formatMoney(item.value)}
              />
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Movimientos del día"
        subtitle="Pagos capturados durante la jornada actual."
      >
        <SimpleTable
          mobile={isMobile}
          headers={["Hora", "Método", "Monto", "Saldo a favor", "Referencia", "Notas"]}
          rows={payments.map((item) => [
            formatDateTime(item.createdAt),
            methodLabel(item.method),
            formatMoney(item.amount || 0),
            formatMoney(item.unallocatedAmount || 0),
            item.referenceType || "-",
            item.notes || "-"
          ])}
          emptyLabel="No hay pagos registrados para este día."
        />
      </SectionCard>

      <div
        style={{
          ...historyGridStyle
        }}
      >
        <SectionCard
          title="Historial de cortes"
          subtitle="Auditoría del comportamiento de caja en el rango seleccionado."
        >
          <div style={{ display: "grid", gap: 14 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(220px, 260px))",
                gap: isMobile ? 10 : 12
              }}
            >
              <div>
                <FieldLabel>Desde</FieldLabel>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div>
                <FieldLabel>Hasta</FieldLabel>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>

            {loadingHistory ? (
              <StateLine text="Cargando historial..." />
            ) : (
              <SimpleTable
                mobile={isMobile}
                headers={[
                  "Día de caja",
                  "Apertura",
                  "Cierre",
                  "Estado",
                  "Monto inicial",
                  "Esperado",
                  "Contado",
                  "Diferencia",
                  "Abrió",
                  "Cerró"
                ]}
                rows={history.map((item) => [
                  formatBusinessDate(item.businessDate),
                  formatDateTime(item.openedAt),
                  formatDateTime(item.closedAt),
                  sessionStatusLabel(item.status),
                  formatMoney(item.openingAmount || 0),
                  formatMoney(item.expectedCash || 0),
                  formatMoney(item.countedCash || 0),
                  <span style={{ fontWeight: 700, color: diffColor(item.difference) }}>
                    {formatMoney(item.difference || 0)}
                  </span>,
                  item.openedByName || "-",
                  item.closedByName || "-"
                ])}
                emptyLabel="Sin cortes registrados en ese rango."
              />
            )}
          </div>
        </SectionCard>

        <div style={{ display: "grid", gap: 16 }}>
          <SectionCard
            title="Alertas del rango"
            subtitle="Incidencias y sesiones que requieren atención."
          >
            {loadingAlerts ? (
              <StateLine text="Cargando alertas..." />
            ) : !alerts ? (
              <StateLine text="Sin información de alertas." />
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                    gap: 12
                  }}
                >
                  <CompactMetric label="Sesiones revisadas" value={String(alerts.summary?.totalSessions || 0)} />
                  <CompactMetric label="Con diferencia" value={String(alerts.summary?.sessionsWithDifference || 0)} tone="warning" />
                  <CompactMetric label="Observaciones" value={String(alerts.summary?.warningSessions || 0)} tone="warning" />
                  <CompactMetric label="Críticas" value={String(alerts.summary?.criticalSessions || 0)} tone="danger" />
                </div>

                <SimpleTable
                  headers={["Día", "Clínica", "Cerró", "Diferencia", "Severidad"]}
                  rows={(alerts.flaggedSessions || []).slice(0, 6).map((item) => [
                    formatBusinessDate(item.businessDate),
                    item.clinicName || "-",
                    item.closedByName || "-",
                    <span style={{ fontWeight: 700, color: diffColor(item.difference) }}>
                      {formatMoney(item.difference || 0)}
                    </span>,
                    <span style={{ fontWeight: 700, color: severityColor(item.severity) }}>
                      {severityLabel(item.severity)}
                    </span>
                  ])}
                  emptyLabel="Sin cierres observados en el rango."
                />
              </div>
            )}
          </SectionCard>

          <SectionCard
            title="Diferencias por usuario"
            subtitle="Quién concentra la mayor variación en caja."
          >
            {loadingDifferencesByUser ? (
              <StateLine text="Cargando análisis..." />
            ) : (
              <SimpleTable
                mobile={isMobile}
                headers={["Usuario", "Cortes", "Diferencia total", "Positivas", "Negativas"]}
                rows={differencesByUser.map((item) => [
                  item.userName || "-",
                  String(item.sessions || 0),
                  <span style={{ fontWeight: 700, color: diffColor(item.totalDifference) }}>
                    {formatMoney(item.totalDifference || 0)}
                  </span>,
                  String(item.positiveDifferences || 0),
                  String(item.negativeDifferences || 0)
                ])}
                emptyLabel="Sin diferencias registradas en ese rango."
              />
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  )
}

const inputStyle = {
  width: "100%",
  padding: "11px 13px",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  outline: "none",
  background: "#fff",
  boxSizing: "border-box"
}

const textareaStyle = {
  ...inputStyle,
  minHeight: 92,
  resize: "vertical",
  lineHeight: 1.45
}

const buttonStyle = {
  minHeight: 40,
  padding: "9px 13px",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  fontWeight: 800,
  fontSize: 13,
  cursor: "pointer",
  background: "#ffffff",
  color: "#0f172a",
  boxShadow: "0 6px 18px rgba(15,23,42,0.04)"
}

function HeroCard({ title, subtitle, actions, children }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
        borderRadius: 20,
        padding: 18,
        boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
        display: "grid",
        gap: 16
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 14,
          alignItems: "flex-start",
          flexWrap: "wrap"
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              padding: "6px 10px",
              borderRadius: 999,
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              color: "#1d4ed8",
              fontSize: 10.5,
              fontWeight: 900,
              letterSpacing: 0.45,
              textTransform: "uppercase",
              marginBottom: 10
            }}
          >
            Operación de caja
          </div>

          <div
            style={{
              fontSize: 30,
              fontWeight: 900,
              color: "#0f172a",
              letterSpacing: "-0.04em",
              lineHeight: 1.02
            }}
          >
            {title}
          </div>

          <div
            style={{
              color: "#64748b",
              marginTop: 6,
              fontSize: 13,
              lineHeight: 1.4,
              maxWidth: 760
            }}
          >
            {subtitle}
          </div>
        </div>

        {actions}
      </div>

      {children}
    </div>
  )
}

function SectionCard({ title, subtitle, children }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        borderRadius: 18,
        padding: 16,
        boxShadow: "0 8px 22px rgba(15,23,42,0.04)"
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            fontSize: 17,
            fontWeight: 900,
            color: "#0f172a",
            letterSpacing: "-0.02em"
          }}
        >
          {title}
        </div>

        {subtitle ? (
          <div
            style={{
              fontSize: 12.5,
              color: "#64748b",
              marginTop: 4,
              lineHeight: 1.4
            }}
          >
            {subtitle}
          </div>
        ) : null}
      </div>

      {children}
    </div>
  )
}

function HeroMetric({ label, value, tone = "neutral" }) {
  const tones = {
    neutral: { bg: "#ffffff", border: "#e2e8f0", value: "#0f172a" },
    success: { bg: "#f0fdf4", border: "#bbf7d0", value: "#166534" },
    warning: { bg: "#fffbeb", border: "#fde68a", value: "#92400e" }
  }

  const visual = tones[tone] || tones.neutral

  return (
    <div
      style={{
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        borderRadius: 16,
        padding: 14
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          color: "#64748b",
          textTransform: "uppercase",
          fontWeight: 900,
          letterSpacing: 0.35
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 22,
          fontWeight: 900,
          color: visual.value,
          lineHeight: 1.05
        }}
      >
        {value}
      </div>
    </div>
  )
}

function CompactMetric({ label, value, tone = "neutral" }) {
  const tones = {
    neutral: { bg: "#ffffff", border: "#e2e8f0", value: "#0f172a" },
    warning: { bg: "#fffbeb", border: "#fde68a", value: "#92400e" },
    danger: { bg: "#fef2f2", border: "#fecaca", value: "#991b1b" },
    success: { bg: "#f0fdf4", border: "#bbf7d0", value: "#166534" }
  }

  const visual = tones[tone] || tones.neutral

  return (
    <div
      style={{
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        borderRadius: 14,
        padding: 12
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          color: "#64748b",
          textTransform: "uppercase",
          fontWeight: 900,
          letterSpacing: 0.35
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 20,
          fontWeight: 900,
          color: visual.value,
          lineHeight: 1.05
        }}
      >
        {value}
      </div>
    </div>
  )
}

function DifferenceCard({ title, value, helper }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        background: "#f8fafc",
        borderRadius: 14,
        padding: 12
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          color: "#64748b",
          textTransform: "uppercase",
          fontWeight: 900,
          letterSpacing: 0.35
        }}
      >
        {title}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 22,
          fontWeight: 900,
          color: diffColor(value),
          lineHeight: 1.05
        }}
      >
        {formatMoney(value)}
      </div>

      <div
        style={{
          marginTop: 4,
          fontSize: 12.5,
          color: "#64748b",
          lineHeight: 1.4
        }}
      >
        {helper}
      </div>
    </div>
  )
}

function InlineInfoGrid({ items }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        gap: 10
      }}
    >
      {items.map(([label, value]) => (
        <div
          key={label}
          style={{
            border: "1px solid #e2e8f0",
            background: "#f8fafc",
            borderRadius: 14,
            padding: 12
          }}
        >
          <div
            style={{
              fontSize: 11.5,
              color: "#64748b",
              textTransform: "uppercase",
              fontWeight: 900,
              letterSpacing: 0.35
            }}
          >
            {label}
          </div>
          <div
            style={{
              marginTop: 6,
              fontSize: 14,
              fontWeight: 800,
              color: "#0f172a",
              lineHeight: 1.35
            }}
          >
            {value}
          </div>
        </div>
      ))}
    </div>
  )
}

function FieldLabel({ children }) {
  return (
    <label
      style={{
        display: "block",
        marginBottom: 6,
        fontSize: 12,
        color: "#64748b",
        fontWeight: 800
      }}
    >
      {children}
    </label>
  )
}

function StateCard({ tone = "neutral", title, children }) {
  const tones = {
    neutral: { bg: "#ffffff", border: "#e2e8f0", color: "#334155" },
    danger: { bg: "#fef2f2", border: "#fecaca", color: "#991b1b" }
  }

  const visual = tones[tone] || tones.neutral

  return (
    <div
      style={{
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        color: visual.color,
        borderRadius: 16,
        padding: 16,
        boxShadow: "0 8px 22px rgba(15,23,42,0.04)"
      }}
    >
      <div style={{ fontWeight: 900, marginBottom: 4 }}>{title}</div>
      <div style={{ lineHeight: 1.45 }}>{children}</div>
    </div>
  )
}

function StateLine({ text }) {
  return <div style={{ color: "#64748b" }}>{text}</div>
}

function SimpleTable({ headers, rows, emptyLabel, mobile = false }) {
  if (!rows.length) {
    return (
      <div
        style={{
          color: "#64748b",
          border: "1px dashed #e2e8f0",
          borderRadius: 14,
          padding: 14,
          background: "#f8fafc"
        }}
      >
        {emptyLabel}
      </div>
    )
  }

  if (mobile) {
    return (
      <div style={{ display: "grid", gap: 10 }}>
        {rows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              background: "#ffffff",
              padding: 12,
              display: "grid",
              gap: 9,
              boxShadow: "0 6px 16px rgba(15,23,42,0.04)",
              minWidth: 0
            }}
          >
            {row.map((cell, cellIndex) => (
              <div
                key={cellIndex}
                style={{
                  display: "grid",
                  gap: 3,
                  minWidth: 0,
                  borderBottom: cellIndex === row.length - 1 ? "none" : "1px solid #f1f5f9",
                  paddingBottom: cellIndex === row.length - 1 ? 0 : 8
                }}
              >
                <div
                  style={{
                    fontSize: 10.5,
                    color: "#64748b",
                    fontWeight: 900,
                    textTransform: "uppercase",
                    letterSpacing: 0.35
                  }}
                >
                  {headers[cellIndex] || "Detalle"}
                </div>
                <div style={{ color: "#334155", fontSize: 13.5, minWidth: 0 }}>
                  {cell}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div
      style={{
        overflowX: "auto",
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        background: "#ffffff"
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th
                key={`${header}-${index}`}
                style={{
                  textAlign: "left",
                  fontSize: 11.5,
                  color: "#64748b",
                  borderBottom: "1px solid #e2e8f0",
                  padding: "10px 8px",
                  whiteSpace: "nowrap",
                  background: "#f8fafc",
                  textTransform: "uppercase",
                  letterSpacing: 0.35
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {row.map((cell, idx) => (
                <td
                  key={idx}
                  style={{
                    padding: "10px 8px",
                    borderBottom: "1px solid #f1f5f9",
                    fontSize: 14,
                    color: "#334155",
                    verticalAlign: "top",
                    whiteSpace: "nowrap"
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
